import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, lkAuth } from "@flame/shared/api";
import { QRCode, CopyButton, Spinner, toast } from "@flame/shared/ui";

/**
 * LK TOTP setup page.
 *
 * Mirrors apps/admin/src/pages/TotpSetupPage.tsx but:
 *  - calls lkAuth.totpSetup/totpVerify (not admin)
 *  - on success → navigate("/") (AccountPage)
 *  - label "FlameVPN — Личный кабинет" via provisioning_uri (issued by backend)
 *  - inputs have explicit text-fg + caret-accent (no black-on-black)
 */
export function TotpSetupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: setup, isLoading, isError } = useQuery({
    queryKey: ["lk-totp-setup"],
    queryFn: lkAuth.totpSetup,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setFieldError("Введите ровно 6 цифр");
      inputRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setFieldError(null);
    try {
      await lkAuth.totpVerify(code);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("2FA включена", "Вход теперь защищён кодом");
      void navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setFieldError("Неверный код — попробуйте ещё раз");
      } else {
        toast.error("Ошибка верификации");
        setFieldError("Ошибка верификации");
      }
      setCode("");
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => { inputRef.current?.focus(); }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !setup) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-bg px-4 text-center">
        <p className="text-sm text-danger">Не удалось загрузить TOTP-секрет.</p>
        <p className="text-xs text-fg-muted">
          Возможно, 2FA уже настроена —{" "}
          <a href="/totp/verify" className="text-accent hover:underline">введите код</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-md rounded-xl bg-surface p-8 shadow-card">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-elevated text-accent">
            <ShieldIcon />
          </div>
          <div>
            <h1 className="text-base font-semibold tracking-tight text-fg">Настройка 2FA</h1>
            <p className="text-xs text-fg-muted">Двухфакторная защита аккаунта</p>
          </div>
        </div>

        <p className="mb-5 text-sm text-fg-muted">
          Отсканируйте QR-код в приложении-аутентификаторе (Google Authenticator, Aegis, 1Password)
          или введите секрет вручную.
        </p>

        <div className="mb-5 flex justify-center rounded-md bg-surface-elevated p-4">
          <QRCode value={setup.provisioning_uri} size={184} />
        </div>

        <div className="mb-6">
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-fg-subtle">
            Или секрет вручную
          </p>
          <div className="flex items-center gap-2 rounded-md border border-border bg-surface-elevated px-3 py-2">
            <code className="mono flex-1 select-all break-all text-xs tracking-wide text-fg">
              {setup.secret}
            </code>
            <CopyButton text={setup.secret} />
          </div>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label
            className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-fg-subtle"
            htmlFor="lk-totp-code"
          >
            Код подтверждения
          </label>
          <input
            ref={inputRef}
            id="lk-totp-code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
              setFieldError(null);
            }}
            autoComplete="one-time-code"
            placeholder="000000"
            className={[
              "mono w-full rounded-md border bg-surface-elevated px-3 py-2.5 text-center text-xl tracking-[0.4em]",
              "text-fg caret-accent placeholder:text-fg-subtle",
              "outline-none transition-colors",
              fieldError ? "border-danger" : "border-border focus:border-accent",
            ].join(" ")}
          />
          {fieldError && (
            <p role="alert" className="mt-1.5 text-xs text-danger">{fieldError}</p>
          )}
          <button
            type="submit"
            disabled={!/^\d{6}$/.test(code) || submitting}
            className="mt-5 w-full rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? "Проверка…" : "Подтвердить и включить"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </svg>
  );
}
